import {
  Button,
  Collapse,
  EditButton,
  Icons,
  Input,
  List,
  Select,
  ShowButton,
  Space,
  Steps,
  Table,
  Tag,
  TextField,
  Tooltip,
  useModalForm,
  useSelect,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  GetListResponse,
  useDelete,
  CrudFilters,
  LogicalFilter,
  useGetIdentity,
  useCan,
  useInvalidate,
} from '@pankod/refine-core'
import dayjs, { Dayjs } from 'dayjs'
import Head from 'next/head'
import React from 'react'

import Filter, { TFilterForm } from '@components/ContentFilter'
import { TCommonError } from 'src/interfaces/common'
import { PopDeleteConfirm } from '../components/PopDeleteConfirm'
import {
  PROGRAM_STATE,
  TLMSProgram,
  TLMSProgramDetail,
  TLMSProgramRequest,
} from './types'
import {
  getAvailableStatus,
  getProgressDot,
  isProgramDeletionAllowed,
} from './utils'
import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import { castAsTuple } from '@resources/lms-management/types'
import EditProgramModalForm from '@resources/lms-management/program/blocks/EditProgramModalForm'
import CreateProgramModalForm from '@resources/lms-management/program/blocks/CreateProgramModalForm'
import { NestedBreadcrumb } from '@resources/lms-management/components/NestedBreadcrumb'
import useTableWithMeta from 'src/hooks/useTableWithMeta'

const PROGRAM_RESOURCE = 'programs'
const PROGRAM_ENTITIES_RESOURCE = 'entities/programs/admin-entitas'

const STATUS_OPTIONS: { label: string; value: TLMSProgram['status'] }[] = [
  {
    label: 'Persiapan',
    value: PROGRAM_STATE.PREPARATION,
  },
  {
    label: 'Berjalan',
    value: PROGRAM_STATE.RUNNING,
  },
  {
    label: 'Selesai',
    value: PROGRAM_STATE.ENDED,
  },
]

export const LMSProgramManagementList: React.FC<
  IResourceComponentsProps<GetListResponse<TLMSProgram>>
> = ({ options }) => {
  const { data, isSuccess } = useGetIdentity()
  const LMSRole = data?.user?.LMSRole || ''
  const invalidate = useInvalidate()
  const { selectProps } = useSelect({
    resource: 'program-tags',
    dataProviderName: 'lms',
    optionLabel: 'name',
    optionValue: 'id',
    defaultValueQueryOptions: {
      enabled: false,
    },
    filters: [
      {
        field: 'pageSize',
        operator: 'eq',
        value: -1,
      },
    ],
  })
  const { tableProps, searchFormProps, filters } = useTableWithMeta<
    TLMSProgram,
    TCommonError,
    TFilterForm<TLMSProgram & { tagIds: string }>
  >({
    syncWithLocation: true,
    queryOptions: {
      enabled: isSuccess,
    },
    resource:
      LMSRole === 'admin-entitas'
        ? PROGRAM_ENTITIES_RESOURCE
        : PROGRAM_RESOURCE,
    dataProviderName: 'lms',
    initialSorter: [
      {
        field: 'endDate',
        order: 'desc',
      },
    ],
    onSearch: (params) => {
      const filters: CrudFilters = []
      const { status, name, tagIds = [] } = params
      const shouldAddTimestamp = [name, status, ...tagIds].some(Boolean)
      filters.push(
        {
          field: 'status',
          operator: 'eq',
          value: status,
        },
        {
          field: 'name',
          operator: 'eq',
          value: name,
        },
        {
          field: 'tagIds',
          operator: 'eq',
          value: tagIds.length ? tagIds : undefined,
        },
        {
          field: 't',
          operator: 'eq',
          value: shouldAddTimestamp ? new Date().getTime() : undefined,
        },
      )
      return filters
    },
    metaData: {
      queryArrayFormat: 'none',
    },
    defaultSetFilterBehavior: 'merge',
  })

  const {
    modalProps: createModalProps,
    formProps: createFormProps,
    show: createShow,
    close: createClose,
    submit: createSubmit,
  } = useModalForm<TLMSProgram, TCommonError, TLMSProgramRequest>({
    action: 'create',
    resource: PROGRAM_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    submit: ({ periode, periodePendaftaran, ...formValues }) => {
      const [startDate, endDate] = castAsTuple<Dayjs>(periode)
      const mappedPeriodePendaftaran = periodePendaftaran
        ? {
            pendaftaranStartDate: castAsTuple<Dayjs>(periodePendaftaran)[0]
              .startOf('day')
              .toISOString(),
            pendaftaranEndDate: castAsTuple<Dayjs>(periodePendaftaran)[1]
              .endOf('day')
              .toISOString(),
          }
        : {}
      return {
        ...formValues,
        startDate: startDate.startOf('day').format('YYYY-MM-DD'),
        endDate: endDate.endOf('day').format('YYYY-MM-DD'),
        ...mappedPeriodePendaftaran,
      }
    },
    redirect: false,
    onMutationSuccess() {
      if (LMSRole === 'admin-entitas') {
        invalidate({
          resource: PROGRAM_ENTITIES_RESOURCE,
          dataProviderName: 'lms',
          invalidates: ['list'],
        })
      }
    },
    successNotification: {
      message: 'Program berhasil dibuat',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(error, 'Terdapat gangguan saat membuat program'),
  })

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editShow,
    submit: editSubmit,
  } = useModalForm<TLMSProgramDetail, TCommonError, TLMSProgramRequest>({
    action: 'edit',
    resource: PROGRAM_RESOURCE,
    dataProviderName: 'lms',
    autoResetForm: true,
    submit: ({ periode, periodePendaftaran, ...formValues }) => {
      const [startDate, endDate] = castAsTuple<Dayjs>(periode)
      const mappedPeriodePendaftaran = periodePendaftaran
        ? {
            pendaftaranStartDate: castAsTuple<Dayjs>(periodePendaftaran)[0]
              .startOf('day')
              .toISOString(),
            pendaftaranEndDate: castAsTuple<Dayjs>(periodePendaftaran)[1]
              .endOf('day')
              .toISOString(),
          }
        : {}
      return {
        blueprintId: formValues.blueprintId,
        name: formValues.name,
        status: formValues.status,
        startDate: startDate.startOf('day').format('YYYY-MM-DD'),
        endDate: endDate.endOf('day').format('YYYY-MM-DD'),
        tagIds: formValues.tags,
        ...mappedPeriodePendaftaran,
      }
    },
    onMutationSuccess() {
      if (LMSRole === 'admin-entitas') {
        invalidate({
          resource: PROGRAM_ENTITIES_RESOURCE,
          dataProviderName: 'lms',
          invalidates: ['list'],
        })
      }
    },
    successNotification: {
      message: 'Program berhasil diubah',
      type: 'success',
      description: 'Sukses',
    },
    errorNotification: (error) =>
      showErrorNotification(error, 'Terdapat gangguan saat mengubah program'),
    redirect: false,
  })

  const { mutateAsync } = useDelete()
  const { data: canEdit } = useCan({
    action: 'edit',
    resource: 'lms-program-management',
  })

  const { data: canDelete } = useCan({
    action: 'delete',
    resource: 'lms-program-management',
  })

  return (
    <>
      <List
        title="LMS Program Management"
        breadcrumb={
          <NestedBreadcrumb
            injectedItems={[
              {
                label: 'Daftar Program',
              },
            ]}
          />
        }
        createButtonProps={{
          children: 'Buat Program Baru',
          onClick: () => {
            createShow()
          },
        }}
      >
        <Head>
          <title>LMS Admin | {options?.label}</title>
        </Head>

        <Collapse className="!mb-4" bordered={false}>
          <Collapse.Panel
            header={
              <>
                {filters?.length ? (
                  <Icons.FilterFilled
                    className="mr-1"
                    style={{ color: '#08c' }}
                  />
                ) : (
                  <Icons.FilterOutlined className="mr-1" />
                )}
                <TextField
                  value="Filter Program"
                  strong={Boolean(filters?.length)}
                />
              </>
            }
            key={'filter-program'}
          >
            <Filter
              layout="vertical"
              formProps={searchFormProps}
              filters={filters as LogicalFilter[]}
              items={[
                {
                  label: 'Status',
                  name: 'status',
                  component: (
                    <Select
                      options={STATUS_OPTIONS}
                      placeholder="Filter status"
                      allowClear
                    />
                  ),
                },
                {
                  label: 'Nama',
                  name: 'name',
                  component: (
                    <Input
                      placeholder="Filter nama"
                      autoComplete="off"
                      allowClear
                    />
                  ),
                },
                {
                  label: 'Tag',
                  name: 'tagIds',
                  component: (
                    <Select
                      {...selectProps}
                      placeholder="Filter tag"
                      allowClear
                      mode="multiple"
                      onSearch={() => {}}
                      filterOption={(input, option) =>
                        (option!.label as unknown as string)
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  ),
                },
              ]}
            />
          </Collapse.Panel>
        </Collapse>

        <Table
          {...tableProps}
          bordered
          rowKey="id"
          scroll={{ x: 800 }}
          tableLayout="fixed"
          size="small"
        >
          <Table.Column
            dataIndex="id"
            title="ID"
            render={(value) => <TextField value={value} />}
            width={100}
          />
          <Table.Column
            dataIndex="name"
            title="Nama Program"
            sorter
            render={(value) => <TextField value={value} />}
            width={250}
          />
          <Table.ColumnGroup title="Rentang Pendaftaran">
            <Table.Column
              dataIndex="pendaftaranStartDate"
              title="Mulai Pendaftaran"
              width={150}
              render={(value) =>
                value ? (
                  <TextField value={dayjs(value).format('DD MMM YYYY')} />
                ) : (
                  <TextField value={'Kosong'} disabled italic />
                )
              }
            />
            <Table.Column
              dataIndex="pendaftaranEndDate"
              title="Pendaftaran Selesai"
              width={150}
              render={(value) =>
                value ? (
                  <TextField value={dayjs(value).format('DD MMM YYYY')} />
                ) : (
                  <TextField value={'Kosong'} disabled italic />
                )
              }
            />
          </Table.ColumnGroup>
          <Table.ColumnGroup title="Periode Program">
            <Table.Column
              dataIndex="startDate"
              title="Tanggal Mulai"
              sorter
              width={150}
              render={(value) => (
                <TextField value={dayjs(value).format('DD MMM YYYY')} />
              )}
            />
            <Table.Column
              dataIndex="endDate"
              title="Tanggal Selesai"
              sorter
              width={150}
              render={(value) => (
                <TextField value={dayjs(value).format('DD MMM YYYY')} />
              )}
            />
          </Table.ColumnGroup>
          <Table.Column
            dataIndex="tags"
            title="Tags"
            width={120}
            render={(value: TLMSProgram['tags']) =>
              value?.length ? (
                <div className="flex flex-wrap gap-1">
                  {value.map(({ id, code, name }) => (
                    <Tooltip key={id} title={name}>
                      <Tag className="!m-0">{code}</Tag>
                    </Tooltip>
                  ))}
                </div>
              ) : (
                <TextField value="Kosong" italic type="secondary" />
              )
            }
          />
          <Table.Column
            dataIndex="status"
            title="Status"
            width={120}
            fixed="right"
            render={(value: TLMSProgram['status']) => {
              const status = getAvailableStatus(value)
              return (
                <Steps
                  direction="vertical"
                  size="small"
                  current={[
                    PROGRAM_STATE.PREPARATION,
                    PROGRAM_STATE.RUNNING,
                    PROGRAM_STATE.ENDED,
                  ].findIndex((v) => v === value)}
                  progressDot={getProgressDot}
                >
                  <Steps.Step
                    title={PROGRAM_STATE.PREPARATION}
                    className="capitalize"
                    status={status.status[0]}
                  />
                  <Steps.Step
                    title={PROGRAM_STATE.RUNNING}
                    className="capitalize"
                    status={status.status[1]}
                  />
                  <Steps.Step
                    title={PROGRAM_STATE.ENDED}
                    className="capitalize"
                    status={status.status[2]}
                  />
                </Steps>
              )
            }}
          />
          <Table.Column<TLMSProgramDetail>
            title="Aksi"
            width={120}
            fixed="right"
            render={(_, record) => {
              const disableDeleteAction = !isProgramDeletionAllowed({
                status: record.status,
                lmsRole: LMSRole,
                hasDeleteAccess: canDelete?.can,
                isOwner: record.programOwner,
              })

              return (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <ShowButton block size="small" recordItemId={record.id}>
                    Lihat
                  </ShowButton>
                  <EditButton
                    size="small"
                    block
                    ignoreAccessControlProvider
                    recordItemId={record.id}
                    disabled={!canEdit?.can && !record.programOwner}
                    onClick={() => {
                      editShow(record.id)
                    }}
                  >
                    Ubah
                  </EditButton>
                  <PopDeleteConfirm
                    title={`Apakah Anda yakin ingin menghapus Program "${record.name}"?`}
                    placeholder="Tulis ulang nama Program"
                    onConfirm={() =>
                      mutateAsync(
                        {
                          id: record.id,
                          resource: PROGRAM_RESOURCE,
                          dataProviderName: 'lms',
                          successNotification: {
                            message: 'Program berhasil dihapus',
                            type: 'success',
                            description: 'Sukses',
                          },
                          errorNotification: (error) =>
                            showErrorNotification(
                              error,
                              'Terdapat gangguan saat menghapus program',
                            ),
                        },
                        {
                          onSettled: () => {
                            if (LMSRole === 'admin-entitas') {
                              invalidate({
                                resource: PROGRAM_ENTITIES_RESOURCE,
                                dataProviderName: 'lms',
                                invalidates: ['list'],
                              })
                            }
                          },
                        },
                      )
                    }
                    okButtonProps={{
                      danger: true,
                      type: 'default',
                    }}
                    okText="Delete"
                    validateValue={record.name}
                    errorMessage="Nama program tidak sama"
                    disabled={disableDeleteAction}
                  >
                    <Button
                      size="small"
                      danger
                      block
                      disabled={disableDeleteAction}
                      icon={<Icons.DeleteOutlined />}
                    >
                      Hapus
                    </Button>
                  </PopDeleteConfirm>
                </Space>
              )
            }}
          />
        </Table>
      </List>

      <EditProgramModalForm
        editFormProps={editFormProps}
        editModalProps={editModalProps}
        onSubmit={editSubmit}
      />

      <CreateProgramModalForm
        createFormProps={createFormProps}
        createModalProps={createModalProps}
        onSubmit={createSubmit}
        onClose={createClose}
        LMSRole={LMSRole}
      />
    </>
  )
}
